import type { ModuleType, ReadCallback } from 'i18next'

import otaClient from '@crowdin/ota-client'
import { setDefaultOptions } from 'date-fns'
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import z from 'zod'

import { env } from '../config/env'
import { dateLocales, zodLocales } from './lib-localizations'
import staticManifest from './manifest.json'

const crowdinLanguageMapping = {
	en: 'en',
	uk: 'uk',
	de: 'de',
	es: 'es-ES',
}
class CrowdinOtaI18next {
	type: ModuleType
	otaClient: otaClient
	staticManifest: typeof staticManifest
	constructor(hash: string) {
		this.type = 'backend'
		this.otaClient = new otaClient(hash)
		this.staticManifest = staticManifest
	}

	read(language: string, namespace: string, callback: ReadCallback) {
		this.otaClient.getManifestTimestamp().then((timestamp) => {
			if (timestamp !== this.staticManifest.timestamp) {
				this.otaClient
					.getStringsByLocale(
						crowdinLanguageMapping?.[
							language as keyof typeof crowdinLanguageMapping
						] || language
					)
					.then((value) => callback(null, value))
					.catch((e) => {
						callback(e, null)
					})
			} else {
				import(`./locales/${language}/${namespace}.json`).then((mod) => {
					callback(null, mod.default)
				})
			}
		})
	}
}

const module = new CrowdinOtaI18next(env.VITE_CROWDIN_DIST_HASH!)

i18n
	.use(module)
	.use(LanguageDetector)
	.use(initReactI18next)
	.init(
		{
			load: 'currentOnly',
			supportedLngs: ['en', 'uk', 'es', 'de'],
			ns: ['fields', 'keys'],
			defaultNS: 'keys',
			detection: {
				order: ['querystring', 'cookie', 'localStorage', 'navigator'],
				caches: ['localStorage', 'cookie'],
			},
			// react-intl-universal used single-brace `{var}` placeholders (ICU style),
			// and all existing Crowdin strings are authored that way. Override i18next's
			// default `{{var}}` delimiters so those strings keep interpolating correctly.
			interpolation: { escapeValue: false, prefix: '{', suffix: '}' },
			react: { useSuspense: true },
		},
		() => {
			z.config((zodLocales[i18n.language] || zodLocales.en)())
			setDefaultOptions({
				locale: dateLocales[i18n.language] || dateLocales.en,
			})
		}
	)

export default i18n
